import { useState } from 'react';
import { 
  Container, 
  Typography, 
  TextField, 
  Button, 
  Box, 
  Paper, 
  Grid2, 
  Snackbar, 
  Alert, 
  CircularProgress,
  Breadcrumbs,
  Link,
  Divider,
  alpha,
  Avatar
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import EmailIcon from '@mui/icons-material/Email';
import BasePage from './BasePage';
import { Link as RouterLink } from 'react-router-dom';

function Contact() {
    const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  
  const [errors, setErrors] = useState({
    name: false,
    email: false,
    subject: false,
    message: false
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  const validateEmail = (email: string) => {
    const regex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    return regex.test(email);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when typing
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({
        ...prev,
        [name]: false
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    const newErrors = {
      name: !formData.name.trim(),
      email: !formData.email.trim() || !validateEmail(formData.email),
      subject: !formData.subject.trim(),
      message: !formData.message.trim()
    };
    
    setErrors(newErrors);
    
    // If any errors, don't submit
    if (Object.values(newErrors).some(error => error)) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSnackbar({
          open: true,
          message: data.message || 'Your message has been sent successfully!',
          severity: 'success'
        });
        
        // Reset form on success
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
      } else {
        setSnackbar({
          open: true,
          message: data.error || 'Failed to send message. Please try again.',
          severity: 'error'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'An error occurred while sending your message.',
        severity: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <BasePage
      title="Contact CribCritic | Get in Touch"
      description="Have questions about CribCritic? Get in touch with our team and we'll be happy to help."
      keywords="contact, support, help, feedback, questions"
      canonicalUrl="/contact"
    >
      <Box 
        sx={{ 
          background: theme => `linear-gradient(145deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.default, 0.9)} 100%)`,
          py: 6
        }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <Breadcrumbs sx={{ mb: 4 }}>
            <Link 
              component={RouterLink} 
              to="/" 
              underline="hover"
              sx={{ 
                display: 'flex',
                alignItems: 'center',
                '&:hover': { color: 'primary.main' }
              }}
            >
              Home
            </Link>
            <Typography color="text.primary">Contact Us</Typography>
          </Breadcrumbs>
          
          <Box sx={{ mb: 5, textAlign: 'center' }}>
            <Typography 
              variant="h3" 
              component="h1" 
              sx={{ 
                fontWeight: 700, 
                mb: 2,
                backgroundImage: theme => `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                backgroundClip: 'text',
                color: 'transparent',
              }}
            >
              Get in Touch
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
              We're here to help with any questions about CribCritic's AI home evaluation services
            </Typography>
          </Box>
          
          <Grid2 container spacing={4} justifyContent="center">
            {/* Contact Form */}
            <Grid2 size={{xs:12, md:10, lg:8}}>
              <Paper 
                id="contact-form"
                elevation={3} 
                sx={{ 
                  p: { xs: 3, md: 4 }, 
                  borderRadius: 2,
                  boxShadow: theme => `0 8px 24px ${alpha(theme.palette.common.black, 0.1)}`
                }}
              >
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  mb: 4
                }}>
                  <Avatar 
                    sx={{ 
                      bgcolor: 'primary.main', 
                      width: 64, 
                      height: 64, 
                      mb: 2,
                      boxShadow: 2
                    }}
                  >
                    <EmailIcon sx={{ fontSize: 32 }} />
                  </Avatar>
                  <Typography variant="h4" component="h2" sx={{ fontWeight: 700, mb: 1 }}>
                    Send a Message
                  </Typography>
                  <Typography variant="body1" color="text.secondary" align="center">
                    We typically respond within 48 hours
                  </Typography>
                </Box>
                
                <Divider sx={{ mb: 4 }} />
                
                <Box component="form" onSubmit={handleSubmit} noValidate>
                  <Grid2 container spacing={3}>
                    <Grid2 size={{xs:12, md:6}}>
                      <TextField
                        fullWidth
                        label="Your Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        error={errors.name}
                        helperText={errors.name ? 'Name is required' : ''}
                        disabled={isSubmitting}
                        variant="outlined"
                        slotProps={{
                          input: { sx: { borderRadius: 2 } }
                        }}
                      />
                    </Grid2>
                    
                    <Grid2 size={{xs:12, md:6}}>
                      <TextField
                        fullWidth
                        label="Email Address"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        error={errors.email}
                        helperText={errors.email ? 'Valid email is required' : ''}
                        disabled={isSubmitting}
                        variant="outlined"
                        slotProps={{
                          input: { sx: { borderRadius: 2 } }
                        }}
                      />
                    </Grid2>
                    
                    <Grid2 size={{xs:12}}>
                      <TextField
                        fullWidth
                        label="Subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        error={errors.subject}
                        helperText={errors.subject ? 'Subject is required' : ''}
                        disabled={isSubmitting}
                        variant="outlined"
                        multiline={false}
                        inputProps={{ id: "subject-field" }}
                        onKeyDown={(e) => {
                          if (e.key === 'Tab' && !e.shiftKey) {
                            e.preventDefault();
                            // Find message field more reliably
                            const messageField = document.getElementById('message-field');
                            if (messageField) {
                              messageField.focus();
                            }
                          }
                        }}
                        slotProps={{
                          input: { sx: { borderRadius: 2 } }
                        }}
                      />
                    </Grid2>
                    
                    <Grid2 size={{xs:12}}>
                      <TextField
                        fullWidth
                        id="message-field"
                        name="message"
                        label="Message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        error={errors.message}
                        helperText={errors.message ? 'Message is required' : ''}
                        disabled={isSubmitting}
                        multiline
                        // rows={6}
                        minRows={4}
                        //maxRows={1}
                        placeholder="Type your message here..."
                        variant="filled"
                        // Use slotProps instead of InputProps (which is deprecated)
                        slotProps={{
                          input: {
                            sx: { borderRadius: 2, backgroundColor: theme => alpha(theme.palette.background.input, 0.8) },
                          }
                        }}
                        sx={{ mb: 2 }}
                      />
                    </Grid2>
                    
                    <Grid2 size={{xs:12}} sx={{ mt: 2, display: 'flex', justifyContent: 'center', position: 'relative' }}>
                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        disabled={isSubmitting}
                        startIcon={isSubmitting ? <CircularProgress size={20} /> : <SendIcon />}
                        sx={{ 
                          minWidth: 220, 
                          py: 1.5,
                          fontWeight: 600,
                          borderRadius: 28,
                          boxShadow: theme => `0 8px 16px ${alpha(theme.palette.primary.main, 0.2)}`,
                          '&:hover': {
                            boxShadow: theme => `0 8px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                            transform: 'translateY(-2px)'
                          },
                          transition: 'all 0.3s ease'
                        }}
                      >
                        {isSubmitting ? 'Sending...' : 'Send Message'}
                      </Button>
                    </Grid2>
                  </Grid2>
                </Box>
              </Paper>
            </Grid2>
          </Grid2>
        </Container>
      </Box>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ 
            width: '100%',
            borderRadius: 2,
            boxShadow: 3
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </BasePage>
  );
}

export default Contact;